/**
 * Tenant Admin → Booking Settlements (`/admin/booking-settlements`).
 *
 * One row per closed booking — see the ``BookingSettlement`` model
 * docstring in ``backend/models.py`` for the math.
 *
 * Layout:
 *   • 4-card KPI strip (Pending count + ₹ tenant owes / merchant owes / settled count)
 *   • Filter chips: ALL / PENDING / PAID + per-shop filter dropdown
 *   • Per-booking ledger table with "Mark paid" CTA on PENDING rows
 *
 * Sourced from:
 *   GET  /api/admin/booking-settlements?status=&restaurant_id=
 *   POST /api/admin/booking-settlements/{sid}/pay  body: { payout_ref }
 */
import { useEffect, useMemo, useState } from "react";
import {
    Wallet, IndianRupee, Receipt, AlertCircle, RefreshCw,
    CheckCircle2, Loader2, Store, Calendar, ArrowRight, ArrowLeft,
} from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import AdminShopPicker from "../../components/AdminShopPicker";

const STATUS_FILTERS = ["ALL", "PENDING", "PAID", "DISPUTED"];

const STATUS_PILL = {
    PENDING: "bg-amber-100 text-amber-800",
    PAID: "bg-emerald-100 text-emerald-800",
    DISPUTED: "bg-red-100 text-red-700",
};

export default function AdminBookingSettlements() {
    const [rows, setRows] = useState(null);
    const [totals, setTotals] = useState({
        count: 0, tenant_owes_merchant: 0, merchant_owes_tenant: 0,
        settled_count: 0, pending_count: 0,
    });
    const [shops, setShops] = useState([]);
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [shopFilter, setShopFilter] = useState("");
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        try {
            const params = new URLSearchParams();
            if (statusFilter !== "ALL") params.set("status", statusFilter);
            if (shopFilter) params.set("restaurant_id", shopFilter);
            const qs = params.toString();
            const { data } = await api.get(`/admin/booking-settlements${qs ? `?${qs}` : ""}`);
            setRows(data.rows || []);
            setTotals(data.totals || {});
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load");
            setRows([]);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, shopFilter]);

    useEffect(() => {
        api.get("/admin/restaurants")
            .then(({ data }) => setShops(data || []))
            .catch(() => setShops([]));
    }, []);

    const shopName = useMemo(() => {
        const m = {};
        for (const s of shops) m[s.id] = s.name;
        return m;
    }, [shops]);

    const markPaid = async (row) => {
        const ref = window.prompt(
            "Payment reference (UPI ID / bank txn ID — optional)",
        ) || "";
        setBusyId(row.id);
        try {
            await api.post(`/admin/booking-settlements/${row.id}/pay`, {
                payout_ref: ref || undefined,
            });
            toast.success("Marked PAID");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div data-testid="admin-booking-settlements-page">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold inline-flex items-center gap-2">
                        <Wallet size={22} /> Booking Settlements
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Per-booking reconciliation between this tenant and each merchant.
                        Tenant holds the advance · merchant collects the balance at service · platform pays out the net.
                    </p>
                </div>
                <button
                    data-testid="bs-refresh"
                    onClick={load}
                    className="inline-flex items-center gap-1.5 text-xs font-bold bg-muted rounded-xl px-3 py-2"
                >
                    <RefreshCw size={13} /> Refresh
                </button>
            </div>

            {/* KPI strip */}
            <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="bs-kpis">
                <KPI
                    testid="bs-kpi-pending-count"
                    icon={<AlertCircle size={18} className="text-amber-500" />}
                    label="Pending"
                    value={totals.pending_count || 0}
                    sublabel="bookings awaiting payout"
                />
                <KPI
                    testid="bs-kpi-tenant-owes"
                    icon={<ArrowRight size={18} className="text-emerald-500" />}
                    label="Tenant owes merchants"
                    value={inr(totals.tenant_owes_merchant || 0)}
                    sublabel="net to send out"
                    emphasis="emerald"
                />
                <KPI
                    testid="bs-kpi-merchant-owes"
                    icon={<ArrowLeft size={18} className="text-rose-500" />}
                    label="Merchants owe tenant"
                    value={inr(totals.merchant_owes_tenant || 0)}
                    sublabel="net to collect back"
                    emphasis="rose"
                />
                <KPI
                    testid="bs-kpi-settled"
                    icon={<CheckCircle2 size={18} className="text-blue-500" />}
                    label="Settled"
                    value={totals.settled_count || 0}
                    sublabel="closed bookings"
                />
            </div>

            {/* Filters */}
            <div className="mt-5 flex flex-wrap gap-2 items-center">
                <div className="flex gap-2 overflow-x-auto no-scrollbar">
                    {STATUS_FILTERS.map((s) => (
                        <button
                            key={s}
                            data-testid={`bs-filter-${s}`}
                            onClick={() => setStatusFilter(s)}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-full border whitespace-nowrap ${
                                statusFilter === s
                                    ? "bg-foreground text-background border-foreground"
                                    : "border-border"
                            }`}
                        >{s}</button>
                    ))}
                </div>
                <div className="ml-auto">
                    <AdminShopPicker
                        testIdPrefix="bs-shop"
                        value={shopFilter}
                        onChange={setShopFilter}
                        allShopsOption
                        shopsOverride={shops}
                    />
                </div>
            </div>

            {/* Table */}
            <div className="mt-5">
                {rows === null ? (
                    <div className="text-center py-12 text-muted-foreground">
                        <Loader2 size={22} className="inline animate-spin" />
                    </div>
                ) : rows.length === 0 ? (
                    <div
                        data-testid="bs-empty"
                        className="text-center py-12 text-muted-foreground bg-card border border-dashed border-border rounded-2xl"
                    >
                        <Receipt size={28} className="inline opacity-40" />
                        {statusFilter === "ALL" ? (
                            <>
                                <p className="mt-2">No booking settlements yet.</p>
                                <p className="text-xs mt-1">
                                    Rows appear here the moment a merchant taps &quot;Collect cash &amp; close&quot; on a booking.
                                </p>
                            </>
                        ) : (
                            // iter106 — filter-aware empty state so admins don't
                            // think the screen "went blank" after settling the
                            // last row on a filtered tab (the row simply moved
                            // to another status bucket).
                            <>
                                <p className="mt-2">
                                    No <b>{statusFilter.toLowerCase()}</b> booking settlements
                                    {shopFilter ? " for this shop" : ""}.
                                </p>
                                <p className="text-xs mt-1">
                                    {statusFilter === "PENDING"
                                        ? "All caught up! Nothing to pay out right now."
                                        : `No ${statusFilter.toLowerCase()} rows on this filter.`}
                                </p>
                                <button
                                    data-testid="bs-empty-view-all"
                                    onClick={() => setStatusFilter("ALL")}
                                    className="mt-3 text-xs font-semibold text-primary underline underline-offset-2"
                                >
                                    View all settlements →
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                    <ul className="space-y-2" data-testid="bs-rows">
                        {rows.map((r) => (
                            <SettlementRow
                                key={r.id}
                                row={r}
                                shopName={r.restaurant_name || shopName[r.restaurant_id] || "—"}
                                busy={busyId === r.id}
                                onMarkPaid={() => markPaid(r)}
                            />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function KPI({ icon, label, value, sublabel, testid, emphasis }) {
    const valueClass =
        emphasis === "emerald" ? "text-emerald-700 dark:text-emerald-400"
        : emphasis === "rose" ? "text-rose-700 dark:text-rose-400"
        : "text-foreground";
    return (
        <div data-testid={testid} className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
                {icon}
            </div>
            <p className={`mt-2 text-2xl font-extrabold ${valueClass}`}>{value}</p>
            {sublabel && <p className="text-[11px] text-muted-foreground mt-0.5">{sublabel}</p>}
        </div>
    );
}

function SettlementRow({ row, shopName, busy, onMarkPaid }) {
    const net = Number(row.net_to_merchant || 0);
    const closedAt = row.closed_at ? new Date(row.closed_at) : null;
    const direction =
        net > 0 ? { label: "Pay merchant", className: "text-emerald-700 dark:text-emerald-400" } :
        net < 0 ? { label: "Collect from merchant", className: "text-rose-700 dark:text-rose-400" } :
        { label: "Fully settled at counter", className: "text-muted-foreground" };

    return (
        <li
            data-testid={`bs-row-${row.id}`}
            className="bg-card border border-border rounded-2xl p-4"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold line-clamp-1">{row.product_name}</h3>
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${STATUS_PILL[row.status] || "bg-muted"}`}>
                            {row.status}
                        </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1.5">
                        <Store size={11} /> {shopName}
                        {closedAt && <><span>·</span><Calendar size={11} /> {closedAt.toLocaleString()}</>}
                    </p>
                </div>
                <div className="text-right">
                    <p className={`text-lg font-extrabold ${direction.className}`}>
                        {Math.abs(net) > 0 ? inr(Math.abs(net)) : inr(0)}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{direction.label}</p>
                </div>
            </div>

            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-y-1 gap-x-3 text-xs">
                <Cell label="Final" value={inr(row.final_amount)} />
                <Cell label="Advance held" value={inr(row.advance_paid)} />
                <Cell label="Commission" value={`${inr(row.commission_amount)} · ${(row.commission_percent || 0).toFixed(1)}%`} />
                <Cell label="Merchant owed" value={inr(row.merchant_owed)} />
            </div>

            {row.status === "PAID" && (
                <div className="mt-3 pt-3 border-t border-border text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                    <CheckCircle2 size={12} className="text-emerald-500" />
                    Paid {row.paid_at ? new Date(row.paid_at).toLocaleString() : "—"}
                    {row.payout_ref && <span className="ml-1">· ref <b className="text-foreground">{row.payout_ref}</b></span>}
                </div>
            )}

            {row.status === "PENDING" && net !== 0 && (
                <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-2">
                    <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                        <IndianRupee size={12} />
                        {net > 0
                            ? "Send the net amount to the merchant outside the platform, then mark this row paid."
                            : "Collect the over-payment back from the merchant, then mark this row settled."}
                    </span>
                    <button
                        data-testid={`bs-mark-paid-${row.id}`}
                        disabled={busy}
                        onClick={onMarkPaid}
                        className="inline-flex items-center gap-1.5 text-xs font-bold bg-primary text-primary-foreground rounded-xl px-3 py-1.5 disabled:opacity-60"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                        Mark paid
                    </button>
                </div>
            )}

            {row.status === "PENDING" && net === 0 && (
                <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-2">
                    <span className="text-[11px] text-muted-foreground">
                        Nothing to transfer — final amount fully covered by advance + counter cash.
                    </span>
                    <button
                        data-testid={`bs-mark-paid-${row.id}`}
                        disabled={busy}
                        onClick={onMarkPaid}
                        className="inline-flex items-center gap-1.5 text-xs font-bold bg-muted rounded-xl px-3 py-1.5 disabled:opacity-60"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                        Close
                    </button>
                </div>
            )}
        </li>
    );
}

function Cell({ label, value }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</p>
            <p className="font-semibold">{value}</p>
        </div>
    );
}
